import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { columns } from './services-grid-columns';
import { servicesApiKeyStore } from '../../../../api/masters/services-api-key-store';
import DeleteIcon from '@mui/icons-material/Delete';
import { ServiceBundleDialog } from '../service-bundle/service-bundle.dialog';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { Service } from '../../../../models/masters/service';
import { serviceBundleApiKeyStore } from '../../../../api/service-bundle-api-key-store';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ServiceBundle } from '../../../../models/service-bundle';
import { ServicesDetails } from './service-details';
import { GridRowProps } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { useDeleteData } from '../../../../hooks/useDeleteData';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { GuardedComponent } from '../../../../components/guarded-component';
import { useAuthorize } from '../../../../hooks/use-authorize';

interface CellProps {
  bundleService: any;
}

interface CustomGridCellProps {
  field: string;
}
interface OfficeTabProps {
  officeID: number;
}
export const ServicesBundleTab = ({ officeID }: OfficeTabProps) => {
  const [openServiceBundle, setOpenServiceBundle] = useState(false);
  const [openServiceList, setOpenServiceList] = useState(false);
  const [value, setValue] = useState('create');
  const [splitID, setSplitID] = useState(0);
  const [editItem, setEditItem] = useState<ServiceBundle>();
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState<number>(0);
  const { data, isLoading, refetch } = useLoadData<ApiResponse<Service[]>>(
    serviceBundleApiKeyStore.getAllServiceBundle(0, officeID, 0)
  );
  const [currentItemID, setCurrentItemID] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const {
    isLoading: isLoadingBundle,
    isError,
    error,
    mutateAsync: deleteBundle,
  } = useDeleteData(serviceBundleApiKeyStore.deleteServicBundle(currentItemID));

  useEffect(() => {
    const calculateTotalInvoiceAmount = () => {
      const total = data?.data?.reduce((acc, item) => {
        return acc + (Number(item.invoiceAmount) || 0);
      }, 0);
      setTotalInvoiceAmount(!total ? 0 : total);
    };
    if (data) {
      calculateTotalInvoiceAmount();
    }
  }, [data]);

  const handleOpenBundleDialog = () => {
    setOpenServiceBundle(true);
    setValue('create');
    setEditItem(undefined);
  };
  const handleCloseBundleDialog = () => {
    setOpenServiceBundle(false);
  };

  const handleEditClick = (bundleService: any) => {
    setValue('edit');
    setEditItem(bundleService);
    setOpenServiceBundle(true);
  };

  const handleCountCellClick = (bundleService: any) => {
    setSplitID(bundleService?.typeID);
    setOpenServiceList(true);
  };

  const openDialog = (id: any) => {
    setIsOpen(true);
    setCurrentItemID(id);
  };

  const FooterCell = () => {
    return (
      <td>
        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
          Total: {!!totalInvoiceAmount && `$${totalInvoiceAmount?.toFixed(2)}`}
        </Typography>
      </td>
    );
  };

  const SplitAmount = ({ bundleService }: CellProps) => {
    return (
      <td>
        <Typography variant="body1" style={{ cursor: 'pointer' }}>
          {!!bundleService?.splitAmount && `$${bundleService?.splitAmount}`}
        </Typography>
      </td>
    );
  };

  const BundlePrice = ({ bundleService }: CellProps) => {
    return (
      <td>
        <Typography variant="body1" style={{ cursor: 'pointer' }}>
          {!!bundleService?.bundlePrice && `$${bundleService?.bundlePrice}`}
        </Typography>
      </td>
    );
  };

  const InvoiceAmount = ({ bundleService }: CellProps) => {
    return (
      <td>
        <Typography variant="body1" style={{ cursor: 'pointer' }}>
          {!!bundleService?.invoiceAmount && `$${bundleService?.invoiceAmount}`}
        </Typography>
      </td>
    );
  };

  const BundleItem = ({ bundleService }: CellProps) => {
    return (
      <td>
        <Typography
          variant="body1"
          style={{
            fontWeight: bundleService?.itemType === 'Bundle' || !bundleService?.itemType ? 'bold' : '',
            marginLeft: bundleService?.itemType === 'Bundle' || !bundleService?.itemType ? 0 : 17,
          }}
        >
          {bundleService?.typeName}
        </Typography>
      </td>
    );
  };

  const CountItem = ({ bundleService }: CellProps) => {
    return (
      <td>
        <IconButton aria-label="edit" onClick={() => handleCountCellClick(bundleService)}>
          <Typography variant="body1" style={{ cursor: 'pointer' }}>
            {bundleService?.itemType === 'Split' && bundleService?.servicesCount}
          </Typography>
        </IconButton>
      </td>
    );
  };

  const isEditPermission = useAuthorize([UserPermissionName.EDIT_SERVICE_BUNDLE]);
  const isDeletePermission = useAuthorize([UserPermissionName.DELETE_SERVICE_BUNDLE]);

  const EditCell = ({ bundleService }: CellProps) => {
    return (
      <td style={{ display: 'flex' }}>
        {bundleService?.itemType === 'Bundle' && (
          <>
            <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_SERVICE_BUNDLE]}>
              <IconButton
                disabled={!bundleService?.isEditable}
                aria-label="edit"
                onClick={() => handleEditClick(bundleService)}
              >
                <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                  <EditIcon className="btnEdit" />
                </Tooltip>
              </IconButton>
            </GuardedComponent>
            {!isEditPermission && (
              <Tooltip title={<Typography variant="body1">You do not have edit permission</Typography>}>
                <EditIcon style={{ color: 'gray' }} className="btnEdit" />
              </Tooltip>
            )}
            <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_SERVICE_BUNDLE]}>
              <IconButton style={{ padding: 5 }} aria-label="delete" onClick={() => openDialog(bundleService?.typeID)}>
                <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                  <DeleteIcon className="btnDelete" />
                </Tooltip>
              </IconButton>
            </GuardedComponent>
            {!isDeletePermission && (
              <Tooltip title={<Typography variant="body1">You do not have delete permission</Typography>}>
                <DeleteIcon style={{ color: 'gray' }} className="btnDelete" />
              </Tooltip>
            )}
          </>
        )}
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'typeName') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <BundleItem bundleService={dataItem.dataItem} />;
      };
    }

    if (field === 'servicesCount') {
      return (dataItem: any) => {
        return <CountItem bundleService={dataItem.dataItem} />;
      };
    }

    if (field === 'splitAmount') {
      return (dataItem: any) => {
        return <SplitAmount bundleService={dataItem.dataItem} />;
      };
    }
    if (field === 'bundlePrice') {
      return (dataItem: any) => {
        return <BundlePrice bundleService={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmount') {
      return (dataItem: any) => {
        return <InvoiceAmount bundleService={dataItem.dataItem} />;
      };
    }

    if (field === 'option') {
      return (dataItem: any) => {
        return <EditCell bundleService={dataItem.dataItem} />;
      };
    }
    // Return undefined if the field is not 'bluePrint'
    return undefined;
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps): React.ReactNode => {
    const { dataItem, dataIndex } = props;
    const isBundle = dataItem.itemType === 'Bundle';
    const isSplit = dataItem.itemType === 'Split';
    // const isFirstBundle = isBundle && dataIndex % 2 === 0;

    const green = { backgroundColor: 'rgba(55, 180, 0, 0.32)' };
    const red = { backgroundColor: 'rgba(243, 23, 0, 0.32)' };
    const blue = { backgroundColor: '#B9D9EB' };

    // Check if the previous row was a bundle
    const prevRowIsBundle = dataIndex > 0 && props.dataItem[dataIndex - 1]?.itemType === 'Bundle';

    // Determine the background color based on whether it's the first bundle or the color of the previous row
    const trProps: any = { style: isSplit ? green : isBundle ? red : blue };

    // Clone the trElement with additional props
    const modifiedTrElement = React.cloneElement(trElement, { ...trElement.props, ...trProps });

    // Return the modified trElement
    return modifiedTrElement;
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const submitHanlder = async () => {
    await deleteBundle();
    refetch();
    closeDialog();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div className="borderTop">
      <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE_BUNDLE]}>
        <Button
          className="innerTab-button"
          size="small"
          variant="contained"
          disableElevation={true}
          onClick={handleOpenBundleDialog}
        >
          Add SERVICE BUNDLE
        </Button>
      </GuardedComponent>
      <SiTrackGrid
        fileTitle='office_service_bundle'
        columns={columns}
        dataItemKey="typeID"
        customCell={(field) =>
          CustomGridCell({
            field,
          })
        }
        showWithoutSorting={true}
        dataItems={data?.data}
        isBusy={isLoading}
        footerCell={FooterCell}
        actionPermissions={{
          export: UserPermissionName.DOWNLOAD_GRID,
        }}
      // rowRender={rowRender}
      />
      {openServiceBundle && (
        <ServiceBundleDialog
          value={value}
          setValue={setValue}
          officeID={officeID}
          open={openServiceBundle}
          onClose={handleCloseBundleDialog}
          editItem={editItem}
          setEditItem={setEditItem}
          refetch={refetch}
        />
      )}
      {openServiceList && (
        <ServicesDetails splitID={splitID} open={openServiceList} onClose={() => setOpenServiceList(false)} />
      )}
      {isOpen && (
        <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={closeDialog}>
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>Are you sure you want to permanently delete?</div>
              <LoadingIndicator isLoading={isLoadingBundle} />
              {/* <ApiErrorComponent isError={isError} errorRespnse={error} /> */}
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button className="btn btn-sm btn-default btn-lightGrey" onClick={closeDialog} autoFocus>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-danger btn-delete"
                type="button"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                  color: isReadOnlyPermission ? 'black' : '#fff'
                }}
                onClick={submitHanlder}
                disabled={isLoading || isReadOnlyPermission}
              >
                Delete
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
    // <SiTrackGrid
    //   columns={columns}
    //   dataItemKey="serviceID"
    //   queryConfig={servicesApiKeyStore.getServiceOfficeMappings(officeID)}
    // />
  );
};
